import { buildGrid, inside, shortestRunOn } from '../s3/arenaGrid';
import {
  arenaCrowdDefaults,
  arenaGrabDefaults,
  arenaRulesDefaults,
  arenaTemplateDefaults,
  lureSitesDefaults,
  scenarioArenaDefaults,
  vehicleClingDefaults,
  vehicleDefaults,
  vehicleFuelDefaults,
  vehicleMountDefaults,
  vehicleNoiseDefaults,
  vehicleRamDefaults,
  WEAPON_DEFAULTS,
  DEFAULT_QUALITY_TIERS,
} from '../s3/defaults';
import {
  ARENA_EDGES,
  ARENA_OBJECTIVE_KINDS,
  SEAT_ROLES,
  type ArenaPointDef,
  type ArenaRectDef,
  type ArenaRulesDef,
  type ArenaTemplateDef,
  type LureSiteRulesDef,
  type ScenarioArenaDef,
  type VehicleDef,
  type WeaponDef,
} from '../s3/types';
import type { Err, Ids } from './common';
import { filled, fmtNum, idsOf, rangeMsg, sectionList } from './s2common';
import { HELD_MODELS, type Bundle, type BundleKey } from './types';

type At = (path: string, message: string) => void;
type ItemLike = NonNullable<NonNullable<Bundle['items']>[number]>;

/** C# float `ToString(InvariantCulture)` for a template point: "[157, 73]". */
const P = (x: number, y: number) => `[${fmtNum(x)}, ${fmtNum(y)}]`;
const n = (v: number | null | undefined, d = 0) => (typeof v === 'number' ? v : d);

/**
 * Port of shared-cs `ContentValidator.Arena.cs` (`ArenaSections`), S3-ARS: vehicles (with the mount through `VehicleMounts.Weapon`),
 * arena templates (with the worst-seed reachability on ArenaGenerator's grid, `lib/s3/arenaGrid.ts`), arenaRules, lureSites, and a
 * scenario's `arena` block. Same errors, same wording, same order. Absent blocks and fields are the C# initializers.
 */
export function validateArena(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  const items = new Map<string, ItemLike>();
  for (const i of b.items ?? []) if (i && i.id) items.set(i.id, i);
  const zombies = new Set((b.zombies ?? []).map((z) => z?.id ?? '').filter((x) => x !== ''));
  const categories = new Set((b.categories ?? []).map((c) => c?.id ?? '').filter((x) => x !== ''));
  const rules = filled(arenaRulesDefaults(), (b.arenaRules ?? null) as ArenaRulesDef | null);

  const rawVehicles = (b as { vehicles?: (VehicleDef | null)[] | null }).vehicles;
  const vehicles = sectionList(rawVehicles, 'vehicles', err, 'vehicles' as BundleKey);
  const vehicleIds = idsOf(vehicles, (v) => v.id, 'vehicle', err, 'vehicles' as BundleKey);
  for (const [v, i] of vehicles)
    vehicle(b, v, items, (p, m) => err('vehicles' as BundleKey, i, p, m));

  const rawTemplates = (b as { arenaTemplates?: (ArenaTemplateDef | null)[] | null })
    .arenaTemplates;
  const templates = sectionList(rawTemplates, 'arenaTemplates', err, 'arenaTemplates' as BundleKey);
  const templateIds = idsOf(
    templates,
    (t) => t.id,
    'arena template',
    err,
    'arenaTemplates' as BundleKey,
  );
  for (const [t, i] of templates)
    template(t, categories, rules, (p, m) => err('arenaTemplates' as BundleKey, i, p, m));

  arenaRules(rules, zombies, (p, m) => err('arenaRules' as BundleKey, undefined, p, m));
  lure(
    b,
    filled(lureSitesDefaults(), (b.lureSites ?? null) as LureSiteRulesDef | null),
    vehicleIds,
    templates.map(([t]) => t),
    categories,
    rules,
    (p, m) => err('lureSites' as BundleKey, undefined, p, m),
  );

  (b.scenarios ?? []).forEach((s, i) => {
    if (s == null) return;
    const kind = s.kind ?? 'interior';
    const block = (s as { arena?: ScenarioArenaDef | null }).arena;
    const at: At = (p, m) => err('scenarios', i, p, m);
    const isArena = kind === 'arena';
    if (!isArena && block != null)
      at('arena', `Scenario '${s.id ?? ''}' has an arena block but is a ${kind} scenario.`);
    if (isArena) scenarioArena(b, s.id ?? '', block, vehicleIds, templateIds, zombies, rules, at);
  });
}

const positive = (at: At, path: string, v: number, where: string, field: string) => {
  if (!(v > 0)) at(path, `${where} ${field} must be above 0.`);
};
const notNegative = (at: At, path: string, v: number, where: string, field: string) => {
  if (v < 0) at(path, `${where} ${field} is negative.`);
};
const range = (
  at: At,
  path: string,
  v: number,
  min: number,
  max: number,
  where: string,
  field: string,
) => {
  const m = rangeMsg(v, min, max, where, field);
  if (m) at(path, m);
};

/** shared-cs `VehicleMounts.Weapon`: the base item's weapon with every field the mount sets. */
export function mountWeapon(v: VehicleDef, b: Bundle): WeaponDef | null {
  if (v.mount == null) return null;
  const m = filled(vehicleMountDefaults(), v.mount);
  const o: WeaponDef = m.weapon ?? {};
  const baseItem = m.baseItemId
    ? (b.items ?? []).find((i) => i != null && i.id === m.baseItemId)
    : undefined;
  const w: WeaponDef | undefined = baseItem?.weapon ?? undefined;
  const pos = (k: keyof WeaponDef) =>
    n(o[k] as number) > 0 ? n(o[k] as number) : n(w?.[k] as number | undefined);
  const notDef = (k: keyof typeof WEAPON_DEFAULTS & keyof WeaponDef) => {
    const ov = (o[k] as number | undefined) ?? WEAPON_DEFAULTS[k];
    return ov !== WEAPON_DEFAULTS[k] ? ov : ((w?.[k] as number | undefined) ?? WEAPON_DEFAULTS[k]);
  };
  return {
    damage: pos('damage'),
    rangeM: pos('rangeM'),
    fireIntervalS: pos('fireIntervalS'),
    magazineSize: pos('magazineSize'),
    reloadS: pos('reloadS'),
    pellets: notDef('pellets'),
    spreadDeg: pos('spreadDeg'),
    headshotChance: pos('headshotChance'),
    pierce: pos('pierce'),
    pierceFalloff: notDef('pierceFalloff'),
    knockbackM: notDef('knockbackM'),
    heldModel: o.heldModel ? o.heldModel : (w?.heldModel ?? null),
    bloomPerShotDeg: pos('bloomPerShotDeg'),
    maxBloomDeg: pos('maxBloomDeg'),
    fireMoveMult: pos('fireMoveMult'),
    bloomRecoverDegPerS: notDef('bloomRecoverDegPerS'),
    ammoItemId: null,
    durabilityShots: null,
    recoverChance: 0,
    stealthKill: false,
    scope: null,
  };
}

/** `ContentValidator.Penetration` (Combat.cs) for a mount weapon, under the mount's paths. */
function penetration(id: string, w: WeaponDef, at: At) {
  const melee = n(w.magazineSize) === 0 && w.ammoItemId == null;
  const where = `Weapon '${id}'`;
  if (n(w.pierce) < 0) at('mount.weapon.pierce', `${where} pierce can't be negative.`);
  else if (n(w.pierce) > 0 && melee)
    at('mount.weapon.pierce', `${where} is melee and can't pierce.`);
  if ((w as { manualAction?: boolean }).manualAction && melee)
    at(
      'mount.weapon.manualAction',
      `${where} is melee, so it can't be manualAction (a bolt, pump, draw or cock is ranged only).`,
    );
  const fall = w.pierceFalloff ?? WEAPON_DEFAULTS.pierceFalloff;
  if (!(fall >= 0 && fall <= 1))
    at('mount.weapon.pierceFalloff', `${where} needs pierceFalloff in 0..1.`);
  if ((w.knockbackM ?? WEAPON_DEFAULTS.knockbackM) < 0)
    at('mount.weapon.knockbackM', `${where} knockbackM can't be negative.`);
  if (w.heldModel != null && !(HELD_MODELS as readonly string[]).includes(w.heldModel))
    at(
      'mount.weapon.heldModel',
      `${where} has unknown heldModel '${w.heldModel}' (use ${HELD_MODELS.join(', ')}).`,
    );
}

/** `ContentValidator.Bloom` (Holdouts.cs) for a mount weapon. */
function bloom(id: string, w: WeaponDef, at: At) {
  const where = `Weapon '${id}'`;
  const per = n(w.bloomPerShotDeg);
  const max = n(w.maxBloomDeg);
  range(at, 'mount.weapon.bloomPerShotDeg', per, 0, 10, where, 'bloomPerShotDeg');
  range(at, 'mount.weapon.maxBloomDeg', max, 0, 45, where, 'maxBloomDeg');
  range(
    at,
    'mount.weapon.bloomRecoverDegPerS',
    w.bloomRecoverDegPerS ?? WEAPON_DEFAULTS.bloomRecoverDegPerS,
    0,
    200,
    where,
    'bloomRecoverDegPerS',
  );
  range(at, 'mount.weapon.fireMoveMult', n(w.fireMoveMult), 0, 1, where, 'fireMoveMult');
  if (per > 0 && !(max > 0))
    at('mount.weapon.maxBloomDeg', `${where} blooms, so it needs maxBloomDeg above 0.`);
}

function vehicle(b: Bundle, raw: VehicleDef, items: Map<string, ItemLike>, at: At) {
  const v = filled(vehicleDefaults(), raw);
  const where = `Vehicle '${v.id ?? ''}'`;
  if (!v.name || v.name.trim() === '') at('name', `${where} needs a name.`);
  positive(at, 'roadKmh', n(v.roadKmh), where, 'roadKmh');
  notNegative(at, 'tripEnergyPerKm', n(v.tripEnergyPerKm), where, 'tripEnergyPerKm');
  notNegative(at, 'unlockAfterLureRuns', n(v.unlockAfterLureRuns), where, 'unlockAfterLureRuns');
  positive(at, 'topSpeedMps', n(v.topSpeedMps), where, 'topSpeedMps');
  notNegative(at, 'accelMps2', n(v.accelMps2), where, 'accelMps2');
  positive(at, 'turnDegPerS', n(v.turnDegPerS), where, 'turnDegPerS');
  positive(at, 'maxHealth', n(v.maxHealth), where, 'maxHealth');
  positive(at, 'radiusM', n(v.radiusM), where, 'radiusM');
  notNegative(at, 'cargoKg', n(v.cargoKg), where, 'cargoKg');

  const roles: string[] = [];
  const seats = (v.seats ?? []) as (NonNullable<VehicleDef['seats']>[number] | null)[];
  if (seats.length === 0) at('seats', `${where} needs at least one seat (the driver).`);
  seats.forEach((s, i) => {
    if (s == null) {
      at(`seats.${i}`, `${where} has an empty seat.`);
      return;
    }
    const role = s.role ?? '';
    if (!(SEAT_ROLES as readonly string[]).includes(role))
      at(
        `seats.${i}.role`,
        `${where} seat ${i + 1} has unknown role '${role}' (use ${SEAT_ROLES.join(', ')}).`,
      );
    else if (i === 0 && role !== 'driver')
      at(`seats.${i}.role`, `${where} seat 1 must be the driver.`);
    else if (i > 0 && role === 'driver')
      at(`seats.${i}.role`, `${where} has a second driver seat.`);
    const arc = s.arcDeg ?? 360;
    if (arc < 0 || arc > 360)
      at(`seats.${i}.arcDeg`, `${where} seat ${i + 1} needs arcDeg in 0..360.`);
    roles.push(role);
  });

  const noise = filled(vehicleNoiseDefaults(), v.noise ?? null);
  notNegative(at, 'noise.idleM', n(noise.idleM), where, 'noise.idleM');
  if (n(noise.fullM) < n(noise.idleM))
    at('noise.fullM', `${where} noise.fullM must be at least noise.idleM.`);
  notNegative(at, 'noise.hornM', n(noise.hornM), where, 'noise.hornM');
  notNegative(at, 'noise.hornCooldownS', n(noise.hornCooldownS), where, 'noise.hornCooldownS');

  const fuel = filled(vehicleFuelDefaults(), v.fuel ?? null);
  positive(at, 'fuel.tankS', n(fuel.tankS), where, 'fuel.tankS');
  range(at, 'fuel.idleBurn', n(fuel.idleBurn), 0, 1, where, 'fuel.idleBurn');

  const ram = filled(vehicleRamDefaults(), v.ram ?? null);
  notNegative(at, 'ram.minSpeedMps', n(ram.minSpeedMps), where, 'ram.minSpeedMps');
  notNegative(at, 'ram.damageTaken', n(ram.damageTaken), where, 'ram.damageTaken');
  range(at, 'ram.speedKeep', n(ram.speedKeep), 0, 1, where, 'ram.speedKeep');
  notNegative(at, 'ram.bruteDamage', n(ram.bruteDamage), where, 'ram.bruteDamage');
  notNegative(at, 'ram.hitDamage', n(ram.hitDamage), where, 'ram.hitDamage');
  range(at, 'ram.hitSpeedLoss', n(ram.hitSpeedLoss), 0, 1, where, 'ram.hitSpeedLoss');
  if (n(ram.minSpeedMps) > n(v.topSpeedMps) && n(v.topSpeedMps) > 0)
    at(
      'ram.minSpeedMps',
      `${where} ram.minSpeedMps is above its top speed, so it can never ram (use 0).`,
    );

  const cling = filled(vehicleClingDefaults(), v.cling ?? null);
  if (n(cling.max) < 0 || n(cling.max) > 20) at('cling.max', `${where} needs cling.max in 0..20.`);
  positive(
    at,
    'cling.shakeTurnDegPerS',
    n(cling.shakeTurnDegPerS),
    where,
    'cling.shakeTurnDegPerS',
  );
  notNegative(
    at,
    'cling.shakeMinSpeedMps',
    n(cling.shakeMinSpeedMps),
    where,
    'cling.shakeMinSpeedMps',
  );

  if (v.mount != null) {
    const m = filled(vehicleMountDefaults(), v.mount);
    const mw = `${where} mount`;
    const mid = m.id ?? '';
    if (mid.trim() === '') at('mount.id', `${mw} needs an id (hmg_50).`);
    else if (items.has(mid))
      at('mount.id', `${mw} id '${mid}' is an item; a mount weapon is never an item.`);
    const seat = m.seat ?? '';
    if (!roles.includes(seat) || seat === 'driver')
      at('mount.seat', `${mw} seat '${seat}' isn't one of its gunner seats.`);
    positive(at, 'mount.roundsPerRun', n(m.roundsPerRun), mw, 'roundsPerRun');
    notNegative(at, 'mount.noiseM', n(m.noiseM), mw, 'noiseM');
    if (m.baseItemId && !(items.get(m.baseItemId)?.weapon != null))
      at('mount.baseItemId', `${mw} baseItemId '${m.baseItemId}' isn't a weapon item.`);
    const w = mountWeapon(v, b) ?? {};
    positive(at, 'mount.weapon.damage', n(w.damage), mw, 'weapon.damage');
    positive(at, 'mount.weapon.rangeM', n(w.rangeM), mw, 'weapon.rangeM');
    positive(at, 'mount.weapon.fireIntervalS', n(w.fireIntervalS), mw, 'weapon.fireIntervalS');
    positive(at, 'mount.weapon.magazineSize', n(w.magazineSize), mw, 'weapon.magazine');
    penetration(`${v.id ?? ''}.mount`, w, at);
    bloom(`${v.id ?? ''}.mount`, w, at);
  }

  for (const [seat, item] of Object.entries(v.lendsWeapons ?? {})) {
    if (seat === 'driver' || !roles.includes(seat))
      at(
        `lendsWeapons.${seat}`,
        `${where} lends a weapon to seat '${seat}', which isn't one of its gunner seats.`,
      );
    if (!(items.get(item ?? '')?.weapon != null))
      at(`lendsWeapons.${seat}`, `${where} lends '${item ?? ''}', which isn't a weapon item.`);
  }
}

function point(
  where: string,
  what: string,
  path: string,
  p: ArenaPointDef | null | undefined,
  w: number,
  h: number,
  blocks: ArenaRectDef[],
  at: At,
) {
  if (p == null) {
    at(path, `${where} needs a ${what}.`);
    return;
  }
  const x = n(p.x);
  const y = n(p.y);
  if (x < 0 || y < 0 || x > w || y > h)
    at(path, `${where} ${what} at ${P(x, y)} isn't inside the arena.`);
  else if (blocks.some((r) => inside(r, x, y)))
    at(path, `${where} ${what} at ${P(x, y)} is inside a block.`);
}

function template(raw: ArenaTemplateDef, categories: Set<string>, rules: ArenaRulesDef, at: At) {
  const t = filled(arenaTemplateDefaults(), raw);
  const where = `Arena template '${t.id ?? ''}'`;
  const cats = t.categories ?? [];
  if (cats.length === 0) at('categories', `${where} needs at least one building category.`);
  cats.forEach((c, i) => {
    if (!categories.has(c)) at(`categories.${i}`, `${where} has unknown category '${c}'.`);
  });
  const size = t.sizeM ?? [];
  if (size.length !== 2 || size[0]! < 40 || size[1]! < 40 || size[0]! > 1000 || size[1]! > 1000) {
    at('sizeM', `${where} needs sizeM [width, depth], each 40..1000.`);
    return;
  }
  const cell = n(t.cellM);
  if (cell < 0.5 || cell > 10) {
    at('cellM', `${where} needs cellM in 0.5..10.`);
    return;
  }
  const [w, h] = size as [number, number];
  const blocks: ArenaRectDef[] = [];
  (t.blocks ?? []).forEach((r, i) => {
    if (r == null) return;
    blocks.push(r);
    const [x, y, rw, rh] = [n(r.x), n(r.y), n(r.w), n(r.h)];
    if (rw <= 0 || rh <= 0 || x < 0 || y < 0 || x + rw > w || y + rh > h)
      at(`blocks.${i}`, `${where} block '${r.kind ?? ''}' at ${P(x, y)} isn't inside the arena.`);
    if (!r.kind || r.kind.trim() === '')
      at(`blocks.${i}.kind`, `${where} block at ${P(x, y)} needs a kind (a commercial shell).`);
  });

  point(where, 'start', 'start', t.start, w, h, blocks, at);
  const objective = t.objective ?? { at: { x: 0, y: 0 }, kind: 'supply_truck' };
  point(where, 'objective', 'objective.at', objective.at ?? { x: 0, y: 0 }, w, h, blocks, at);
  const okind = objective.kind ?? 'supply_truck';
  if (!(ARENA_OBJECTIVE_KINDS as readonly string[]).includes(okind))
    at('objective.kind', `${where} objective has unknown kind '${okind}'.`);
  (t.wreckSlots ?? []).forEach((p, i) =>
    point(where, 'wreck slot', `wreckSlots.${i}`, p, w, h, blocks, at),
  );
  const cans = t.fuelCanSlots ?? [];
  if (cans.length < 2) at('fuelCanSlots', `${where} needs at least 2 fuelCanSlots.`);
  cans.forEach((p, i) => {
    if (p == null) return;
    if (blocks.some((r) => inside(r, n(p.x), n(p.y))))
      at(`fuelCanSlots.${i}`, `${where} has a fuel can inside a block at ${P(n(p.x), n(p.y))}.`);
    else point(where, 'fuel can', `fuelCanSlots.${i}`, p, w, h, blocks, at);
  });

  const exit = t.exit ?? { x: 0, y: 0, w: 0, h: 0 };
  const [ex, ey, ew, eh] = [n(exit.x), n(exit.y), n(exit.w), n(exit.h)];
  if (ew <= 0 || eh <= 0 || ex < 0 || ey < 0 || ex + ew > w || ey + eh > h)
    at('exit', `${where} exit isn't a rectangle inside the arena.`);

  const spawns = t.spawn ?? [];
  if (spawns.length === 0) at('spawn', `${where} needs at least one spawn group.`);
  let share = 0;
  spawns.forEach((s, i) => {
    if (s == null) return;
    const sh = n(s.share);
    if (sh < 0 || sh > 1) at(`spawn.${i}.share`, `${where} spawn share must be 0..1.`);
    share += Math.max(0, sh);
    if (!s.streets && s.around == null)
      at(`spawn.${i}`, `${where} spawn group needs around (a point) or streets.`);
    if (s.around != null) {
      point(where, 'spawn centre', `spawn.${i}.around`, s.around, w, h, [], at);
      positive(at, `spawn.${i}.radiusM`, n(s.radiusM), where, 'spawn radiusM');
    }
  });
  if (spawns.length > 0 && Math.abs(Math.fround(share) - 1) > 0.01)
    at('spawn', `${where} spawn shares must add up to 1.`);
  (t.reinforceFrom ?? []).forEach((e, i) => {
    if (!(ARENA_EDGES as readonly string[]).includes(e))
      at(
        `reinforceFrom.${i}`,
        `${where} reinforceFrom has unknown edge '${e}' (use north, east, south, west).`,
      );
  });

  // Reachability with every wreck slot filled (the worst seed): the prize and then the exit.
  const g = buildGrid(t, 'all');
  if (!g) return;
  const grab = filled(arenaGrabDefaults(), rules.grab ?? null);
  const line = shortestRunOn(g, t, n(grab.radiusM));
  if (!line.objectiveReachable)
    at('objective.at', `${where} objective can't be reached from the start.`);
  else if (!line.exitReachable) at('exit', `${where} exit can't be reached from the objective.`);
}

function arenaRules(r: ArenaRulesDef, zombies: Set<string>, at: At) {
  const W = 'arenaRules';
  const timeLimit = n(r.timeLimitS);
  const cap = n(r.hordeCap);
  if (timeLimit < 60 || timeLimit > 1800) at('timeLimitS', `${W} needs timeLimitS in 60..1800.`);
  if (cap < 1 || cap > 1000) at('hordeCap', `${W} needs hordeCap in 1..1000.`);
  range(at, 'reinforceMaxShare', n(r.reinforceMaxShare), 0, 1, W, 'reinforceMaxShare');
  let last = Number.MAX_SAFE_INTEGER;
  const tiers =
    r.qualityTiers && r.qualityTiers.length > 0 ? r.qualityTiers : [...DEFAULT_QUALITY_TIERS];
  for (const tier of tiers) {
    if (tier < 1 || tier > Math.max(1, cap))
      at('qualityTiers', `${W} qualityTiers must each be 1..hordeCap.`);
    else if (tier > last) at('qualityTiers', `${W} qualityTiers go high, mid, low (descending).`);
    last = tier;
  }
  const brutes = n(r.maxBrutes);
  if (brutes < 0 || brutes > 20) at('maxBrutes', `${W} needs maxBrutes in 0..20.`);
  const g = filled(arenaGrabDefaults(), r.grab ?? null);
  positive(at, 'grab.radiusM', n(g.radiusM), W, 'grab.radiusM');
  positive(at, 'grab.stopMps', n(g.stopMps), W, 'grab.stopMps');
  notNegative(at, 'grab.baseS', n(g.baseS), W, 'grab.baseS');
  notNegative(at, 'grab.perKgS', n(g.perKgS), W, 'grab.perKgS');
  notNegative(at, 'pickupS', n(r.pickupS), W, 'pickupS');
  notNegative(at, 'fuelCanS', n(r.fuelCanS), W, 'fuelCanS');
  positive(at, 'loseTrackM', n(r.loseTrackM), W, 'loseTrackM');
  notNegative(at, 'loseTrackS', n(r.loseTrackS), W, 'loseTrackS');
  for (const [k, v] of Object.entries(r.clingDps ?? {})) {
    if (!zombies.has(k)) at(`clingDps.${k}`, `${W} clingDps names unknown zombie '${k}'.`);
    notNegative(at, `clingDps.${k}`, n(v), W, `clingDps.${k}`);
  }
  const c = filled(arenaCrowdDefaults(), r.crowd ?? null);
  if (n(c.heroClingers) < 0 || n(c.heroClingers) > 20)
    at('crowd.heroClingers', `${W} needs crowd.heroClingers in 0..20.`);
  positive(at, 'crowd.simHz', n(c.simHz), W, 'crowd.simHz');
  positive(at, 'crowd.farSimHz', n(c.farSimHz), W, 'crowd.farSimHz');
  positive(at, 'crowd.flowHz', n(c.flowHz), W, 'crowd.flowHz');
  notNegative(at, 'crowd.hullWithinM', n(c.hullWithinM), W, 'crowd.hullWithinM');
  const tank = n(r.startTankShare);
  if (!(tank > 0 && tank <= 1))
    at('startTankShare', `${W} needs startTankShare above 0 and at most 1.`);
  range(at, 'runnerShare', n(r.runnerShare), 0, 1, W, 'runnerShare');
  notNegative(at, 'brutesPer100', n(r.brutesPer100), W, 'brutesPer100');
  const slack = n(r.minRunSlack);
  if (!(slack > 0 && slack <= 1))
    at('minRunSlack', `${W} needs minRunSlack above 0 and at most 1.`);
}

function lure(
  b: Bundle,
  l: LureSiteRulesDef,
  vehicles: Set<string>,
  templates: ArenaTemplateDef[],
  categories: Set<string>,
  rules: ArenaRulesDef,
  at: At,
) {
  const W = 'lureSites';
  range(at, 'chancePerSet', n(l.chancePerSet), 0, 1, W, 'chancePerSet');
  if (n(l.maxPerSet) < 0 || n(l.maxPerSet) > 1)
    at('maxPerSet', `${W} needs maxPerSet 0 or 1 (the map's cap is 1 lure run per set).`);
  if (n(l.windowH) < 0.5 || n(l.windowH) > 48) at('windowH', `${W} needs windowH in 0.5..48.`);
  notNegative(at, 'minDistanceM', n(l.minDistanceM), W, 'minDistanceM');
  range(at, 'strandedShare', n(l.strandedShare), 0, 1, W, 'strandedShare');
  range(at, 'overrunShare', n(l.overrunShare), 0, 1, W, 'overrunShare');
  const hs = n(l.strandedHealthShare);
  if (!(hs > 0 && hs <= 1))
    at('strandedHealthShare', `${W} needs strandedHealthShare above 0 and at most 1.`);
  const fp = l.minFootprint ?? '';
  if (fp !== 'small' && fp !== 'medium' && fp !== 'large')
    at('minFootprint', `${W} minFootprint must be small, medium or large.`);
  (l.categories ?? []).forEach((c, i) => {
    if (!categories.has(c)) at(`categories.${i}`, `${W} has unknown category '${c}'.`);
    else if (l.enabled && !templates.some((t) => (t.categories ?? []).includes(c)))
      at(`categories.${i}`, `${W} category '${c}' has no arena template.`);
  });
  const cap = n(rules.hordeCap);
  for (const [k, v] of Object.entries(l.hordeByTier ?? {})) {
    if (k !== 'dense' && k !== 'normal' && k !== 'sparse' && k !== 'empty')
      at(`hordeByTier.${k}`, `${W} hordeByTier has unknown tier '${k}'.`);
    if (n(v) < 1 || n(v) > Math.max(1, cap))
      at(`hordeByTier.${k}`, `${W} hordeByTier.${k} must be 1..hordeCap.`);
  }
  (l.startUnlocked ?? []).forEach((v, i) => {
    if (!vehicles.has(v))
      at(`startUnlocked.${i}`, `${W} startUnlocked names unknown vehicle '${v}'.`);
  });
  for (const [k, v] of Object.entries(l.vehicleWeights ?? {})) {
    if (!vehicles.has(k))
      at(`vehicleWeights.${k}`, `${W} vehicleWeights names unknown vehicle '${k}'.`);
    notNegative(at, `vehicleWeights.${k}`, n(v), W, `vehicleWeights.${k}`);
  }
  const table = l.cargoLootTableId ?? '';
  if (table !== '' && !(b.lootTables ?? []).some((t) => t != null && t.id === table))
    at('cargoLootTableId', `${W} cargoLootTableId '${table}' doesn't exist.`);
  if (n(l.cargoRolls) < 0 || n(l.cargoRolls) > 50)
    at('cargoRolls', `${W} needs cargoRolls in 0..50.`);
  if (!l.enabled) return;
  if (vehicles.size === 0) at('enabled', `${W} is enabled but there are no vehicles.`);
  if (templates.length === 0) at('enabled', `${W} is enabled but there are no arena templates.`);
  if (!l.startUnlocked || l.startUnlocked.length === 0)
    at('startUnlocked', `${W} is enabled but no vehicle is unlocked at the start.`);
  if (table === '' || n(l.cargoRolls) < 1)
    at(
      'cargoLootTableId',
      `${W} is enabled but the supply truck has no cargo (cargoLootTableId and cargoRolls).`,
    );
}

function scenarioArena(
  b: Bundle,
  id: string,
  raw: ScenarioArenaDef | null | undefined,
  vehicles: Set<string>,
  templates: Set<string>,
  zombies: Set<string>,
  rules: ArenaRulesDef,
  at: At,
) {
  const where = `Arena scenario '${id}'`;
  if (raw == null) {
    at('arena', `${where} needs an arena block (templateId, horde, vehicles).`);
    return;
  }
  const a = filled(scenarioArenaDefaults(), raw);
  if (!templates.has(a.templateId ?? ''))
    at('arena.templateId', `${where} template '${a.templateId ?? ''}' doesn't exist.`);
  let total = 0;
  for (const [k, v] of Object.entries(a.horde ?? {})) {
    if (!zombies.has(k)) at(`arena.horde.${k}`, `${where} horde names unknown zombie '${k}'.`);
    if (n(v) < 0) at(`arena.horde.${k}`, `${where} horde.${k} is negative.`);
    total += Math.max(0, n(v));
  }
  const cap = n(rules.hordeCap);
  if (total < 1 || total > Math.max(1, cap))
    at('arena.horde', `${where} horde must total 1..hordeCap (${cap}).`);
  const brutes = (a.horde ?? {})['brute'];
  if (brutes !== undefined && brutes > n(rules.maxBrutes))
    at('arena.horde.brute', `${where} has more brutes than maxBrutes.`);
  (a.vehicles ?? []).forEach((v, i) => {
    if (!vehicles.has(v)) at(`arena.vehicles.${i}`, `${where} lends unknown vehicle '${v}'.`);
  });
  const ok = a.objectiveKind ?? '';
  if (ok !== '' && !(ARENA_OBJECTIVE_KINDS as readonly string[]).includes(ok))
    at('arena.objectiveKind', `${where} has unknown objective kind '${ok}'.`);
  const tl = n(a.timeLimitS);
  if (tl !== 0 && (tl < 60 || tl > 1800))
    at('arena.timeLimitS', `${where} needs timeLimitS 0 or 60..1800.`);
  const table = a.cargoLootTableId ?? '';
  if (table !== '' && !(b.lootTables ?? []).some((t) => t != null && t.id === table))
    at('arena.cargoLootTableId', `${where} cargoLootTableId '${table}' doesn't exist.`);
  if (n(a.cargoRolls) < 0 || n(a.cargoRolls) > 50)
    at('arena.cargoRolls', `${where} needs cargoRolls in 0..50.`);
}
